import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Container, Archive, MapPin } from "lucide-react";
import { useVessels, useArchivedVessels, VesselWithRelations } from "@/hooks/useVessels";
import { useFacilities } from "@/hooks/useFacilities";
import { CreateVesselDialog } from "@/components/vessels/CreateVesselDialog";
import { VesselDetailSheet } from "@/components/vessels/VesselDetailSheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VESSEL_KINDS, TANK_SUBTYPES, formatVesselKind, formatVesselSubtypes } from "@/lib/vessel-types";
import { TankPlanningView } from "@/components/tank-planning/TankPlanningView";

export default function Vessels() {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [subtypeFilter, setSubtypeFilter] = useState("all");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [selectedVessel, setSelectedVessel] = useState<VesselWithRelations | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const activeTab = searchParams.get("tab") || "planning";
  const isPlanning = activeTab !== "vessels";

  const { data: facilities = [] } = useFacilities();
  const { data: allVessels = [], isLoading } = useVessels(searchTerm, undefined, facilityFilter);
  const { data: archivedVessels = [], isLoading: archivedLoading } = useArchivedVessels(searchTerm, undefined, facilityFilter);

  function applyFilters(list: VesselWithRelations[]) {
    let result = list;
    if (kindFilter !== "all") result = result.filter(v => v.vessel_kind === kindFilter);
    if (kindFilter === "tank" && subtypeFilter !== "all") {
      result = result.filter(v => (v.vessel_subtypes || []).includes(subtypeFilter));
    }
    return result;
  }

  const vessels = applyFilters(showArchived ? archivedVessels : allVessels);
  const isLoadingVessels = showArchived ? archivedLoading : isLoading;

  const handleViewDetails = (vessel: VesselWithRelations) => {
    setSelectedVessel(vessel);
    setSheetOpen(true);
  };

  if (isPlanning) {
    return (
      <div className="flex flex-col h-screen">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6 py-4">
          <SidebarTrigger />
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-foreground">Tank Planning</h1>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-hidden">
          <TankPlanningView />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6 py-4">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Vessels</h1>
        </div>
        <CreateVesselDialog />
      </header>

      {/* All Vessels Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-6">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vessels..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={facilityFilter} onValueChange={setFacilityFilter}>
              <SelectTrigger className="w-40">
                <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Facilities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Facilities</SelectItem>
                {facilities.map((facility) => (
                  <SelectItem key={facility.id} value={facility.id}>
                    {facility.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={(v) => { setKindFilter(v); setSubtypeFilter("all"); }}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {VESSEL_KINDS.map(k => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {kindFilter === "tank" && (
              <Select value={subtypeFilter} onValueChange={setSubtypeFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Uses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Uses</SelectItem>
                  {TANK_SUBTYPES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Vessels Grid */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">
                  {showArchived ? "Archived Vessels" : "All Vessels"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {vessels.length} vessel{vessels.length !== 1 ? 's' : ''} {showArchived ? "archived" : "available"}
                </p>
              </div>
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className="gap-2"
              >
                <Archive className="h-4 w-4" />
                {showArchived ? "View Active" : "View Archived"}
                {!showArchived && archivedVessels.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {archivedVessels.length}
                  </Badge>
                )}
              </Button>
            </div>

            {isLoadingVessels ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-48 w-full" />
                ))}
              </div>
            ) : vessels.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground mb-4">No vessels found</p>
                <CreateVesselDialog />
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {vessels.map((vessel) => (
                  <Card
                    key={vessel.id}
                    className="transition-all hover:shadow-md cursor-pointer"
                    onClick={() => handleViewDetails(vessel)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <Container className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{vessel.name}</CardTitle>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="secondary">{formatVesselKind(vessel.vessel_kind)}</Badge>
                              {vessel.vessel_subtypes?.length ? (
                                <span className="text-xs text-muted-foreground">
                                  {formatVesselSubtypes(vessel.vessel_subtypes)}
                                </span>
                              ) : null}
                              {vessel.facility && (
                                <Badge variant="outline" className="gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {vessel.facility.code}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">{formatVesselSubtypes(vessel.vessel_subtypes)}</p>
                        {vessel.owner_client && (
                          <p className="text-xs text-muted-foreground">
                            Owner: {vessel.owner_client.name}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-2xl font-bold text-primary">
                          {vessel.preferred_capacity_unit === 'tons' && vessel.capacity_tons != null
                            ? `${vessel.capacity_tons}T`
                            : vessel.capacity_l != null
                            ? Math.round(vessel.capacity_l).toLocaleString()
                            : vessel.capacity_tons != null
                            ? `${vessel.capacity_tons}T`
                            : "N/A"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {vessel.preferred_capacity_unit === 'tons' && vessel.capacity_tons != null
                            ? "Capacity (Tons)"
                            : vessel.capacity_l != null
                            ? "Capacity (Gal)"
                            : vessel.capacity_tons != null
                            ? "Capacity (Tons)"
                            : "Capacity"}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <VesselDetailSheet
        vessel={selectedVessel}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}

